import React, { Component } from 'react';
import { MapPin, Clock, Info, Bike, ShoppingBag, UtensilsCrossed, Navigation, CalendarClock } from 'lucide-react';
import { Restaurant, RestaurantSettings } from '../../lib/types';
import { formatCurrency } from '../../lib/utils';
import { LanguageSelector } from '../ui/LanguageSelector';
import { useLanguage } from '../../lib/i18n';
interface RestaurantHeaderProps {
  restaurant: Restaurant;
  settings: RestaurantSettings | null;
  zoneName?: string;
}
export function RestaurantHeader({
  restaurant,
  settings,
  zoneName
}: RestaurantHeaderProps) {
  const {
    t
  } = useLanguage();
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isKabu = restaurant.slug === 'kabu';
  const isCongelados = restaurant.slug === 'heladeria-congelados';
  const coverUrl = isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/rYDkPPUtvynEvWDL4HujUk/portada.png';
  const logoUrl = isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isCongelados ? 'https://cdn.magicpatterns.com/uploads/mpAKKGK5uT9Sa68ZhyHdGR/Logo1.png' : restaurant.logo_url;
  return <div className={`${isCongelados ? 'bg-[#FBF5FF] border-[#E8DDF0]' : 'bg-[#FBF7F2] border-[#E8DDD0]'} border-b`} data-id="element-1320">
      {/* Cover Image */}
      <div className={`relative h-44 sm:h-60 md:h-72 w-full bg-[#F5EDE3]`} data-id="element-1321">
        <img src={isCongelados ? 'https://cdn.magicpatterns.com/uploads/ar4FXudFfRgAEWhtKsiDih/ChatGPT_Image_22_abr_2026,_09_55_36.png' : coverUrl} alt={restaurant.name} className="w-full h-full object-cover" data-id="element-1322" />
        <div className={`absolute inset-0 bg-gradient-to-t ${isCongelados ? 'from-[#FBF5FF] via-[#7B2D8E]/40' : 'from-[#FBF7F2] via-black/40'} to-transparent`} data-id="element-1323" />

        {/* Badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end" data-id="element-1324">
          <div className={`bg-white/90 border ${isCongelados ? 'border-[#E8DDF0]' : 'border-[#E8DDD0]'} backdrop-blur-md rounded-lg shadow-lg`} data-id="element-1325">
            <LanguageSelector data-id="element-1326" />
          </div>
          {restaurant.is_featured && <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${isCongelados ? 'bg-[#7B2D8E]' : 'bg-[#8B5E3C]'} text-white shadow-lg`} data-id="element-1327">
              ★ {t('featured')}
            </span>}
          {!restaurant.is_open && <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${isCongelados ? 'bg-[#F472B6]' : 'bg-[#CE1126]'} text-white shadow-lg`} data-id="element-1328">
              {t('closed')}
            </span>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-1329">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-end -mt-10 sm:-mt-14 mb-5" data-id="element-1330">
          {/* Logo */}
          <div className={`relative w-20 h-20 sm:w-28 sm:h-28 rounded-2xl bg-white p-1 shadow-md border ${isCongelados ? 'border-[#E8DDF0]' : 'border-[#E8DDD0]'} shrink-0`} data-id="element-1331">
            {logoUrl ? <img src={logoUrl} alt={`${restaurant.name} logo`} className={`w-full h-full rounded-xl ${isEmilius ? 'object-contain bg-[#F2E6D5]' : isKabu ? 'object-contain bg-[#E0B87B]' : isCongelados ? 'object-contain bg-white' : 'object-cover'}`} data-id="element-1332" /> : <img src="https://cdn.magicpatterns.com/uploads/66K7i7HxpjApVZJyeR9rjp/ChatGPT_Image_15_mar_2026,_13_53_22.png" alt="NOIZA" className="w-full h-full rounded-xl object-cover" data-id="element-1333" />}
          </div>

          {/* Title & Info */}
          <div className="flex-1 pb-2" data-id="element-1334">
            <h1 className={`text-2xl sm:text-4xl font-black ${isCongelados ? 'text-[#7B2D8E]' : 'text-[#3D2B1F]'} mb-1 tracking-tight`} style={{
            fontFamily: isCongelados ? "'Nunito', sans-serif" : "'Playfair Display', serif"
          }} data-id="element-1335">
              {restaurant.name} {isCongelados && '🍦'}
            </h1>
            {restaurant.description_short && <p className={`${isCongelados ? 'text-[#5D3A1A]' : 'text-[#8B7355]'} text-sm sm:text-base max-w-2xl mb-3`} data-id="element-1336">
                {restaurant.description_short}
              </p>}

            <div className={`flex flex-wrap items-center gap-3 text-sm ${isCongelados ? 'text-[#7B2D8E]/80' : 'text-[#8B7355]/70'} font-medium`} data-id="element-1337">
              {(zoneName || restaurant.address_text) && <div className="flex items-center gap-1.5" data-id="element-1338">
                  <MapPin className="w-4 h-4" data-id="element-1339" />
                  {zoneName || restaurant.address_text}
                </div>}

              {settings?.average_prep_time_minutes && <div className="flex items-center gap-1.5" data-id="element-1340">
                  <Clock className="w-4 h-4" data-id="element-1341" />
                  {settings.average_prep_time_minutes} min
                </div>}

              {settings?.minimum_order_amount != null && settings.minimum_order_amount > 0 && <div className="flex items-center gap-1.5" data-id="element-1342">
                    <Info className="w-4 h-4" data-id="element-1343" />
                    Mín. {formatCurrency(settings.minimum_order_amount)}
                  </div>}
            </div>
          </div>
        </div>

        {/* Order Methods — Mexican color gradient badges */}
        {settings && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1344">
            {settings.accept_delivery && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isCongelados ? 'linear-gradient(135deg, #7B2D8E 0%, #F472B6 100%)' : 'linear-gradient(135deg, #006847 0%, #2AABB3 100%)'
        }} data-id="element-1345">
                <Bike className="w-4 h-4" data-id="element-1346" /> {t('delivery')}
              </span>}
            {settings.accept_pickup && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isCongelados ? 'linear-gradient(135deg, #F472B6 0%, #D4A24E 100%)' : 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)'
        }} data-id="element-1347">
                <ShoppingBag className="w-4 h-4" data-id="element-1348" /> {t('pickup')}
              </span>}
            {((settings as any).accept_dine_in || isCongelados) && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isCongelados ? 'linear-gradient(135deg, #4A7FD4 0%, #7B2D8E 100%)' : 'linear-gradient(135deg, #6B2FA0 0%, #CE1126 100%)'
        }} data-id="element-1349">
                <UtensilsCrossed className="w-4 h-4" data-id="element-1350" /> {t('dine_in')}
              </span>}
          </div>}

        {/* Action buttons — Cómo llegar + Horario */}
        {isCongelados && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1351">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #7B2D8E 0%, #4A7FD4 100%)'
        }} data-id="element-1352">
                <Navigation className="w-4 h-4" data-id="element-1353" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => {
          alert('🕐 Horario de Heladería Congelados:\n\nLunes a Viernes: 10:00 AM - 9:00 PM\nSábado y Domingo: 11:00 AM - 10:00 PM\n\n📍 ¡Te esperamos!');
        }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#7B2D8E] border-2 border-[#7B2D8E]/30 bg-white shadow-sm transition-transform active:scale-95 hover:bg-[#7B2D8E]/5" data-id="element-1354">
              <CalendarClock className="w-4 h-4" data-id="element-1355" />
              Horario
            </button>
          </div>}
      </div>
    </div>;
}